import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ConfigFieldWithMode, FieldOption, AvailableVariable } from './ConfigFieldWithMode';

interface FlowControlConfigProps {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  availableVariables?: AvailableVariable[];
  allNodes?: { id: string; label: string }[];
}

export function IfConditionConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Operatore</Label>
        <Select
          value={(config.operator as string) || 'expression'}
          onValueChange={(v) => onChange('operator', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="equals">Uguale a (==)</SelectItem>
            <SelectItem value="not_equals">Diverso da (!=)</SelectItem>
            <SelectItem value="greater">Maggiore di (&gt;)</SelectItem>
            <SelectItem value="greater_equal">Maggiore o uguale (&gt;=)</SelectItem>
            <SelectItem value="less">Minore di (&lt;)</SelectItem>
            <SelectItem value="less_equal">Minore o uguale (&lt;=)</SelectItem>
            <SelectItem value="contains">Contiene</SelectItem>
            <SelectItem value="starts_with">Inizia con</SelectItem>
            <SelectItem value="ends_with">Finisce con</SelectItem>
            <SelectItem value="is_empty">È vuoto</SelectItem>
            <SelectItem value="is_not_empty">Non è vuoto</SelectItem>
            <SelectItem value="expression">Espressione libera</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {config.operator !== 'expression' && config.operator !== 'is_empty' && config.operator !== 'is_not_empty' && (
        <>
          <ConfigFieldWithMode
            label="Valore sinistro"
            fieldKey="left_value"
            value={(config.left_value as string | number) || ''}
            onChange={(v) => onChange('left_value', v)}
            availableVariables={availableVariables}
            placeholder="es: lead.score"
            tooltip="Il valore da confrontare (può essere una variabile)"
            allowAll={false}
          />

          <ConfigFieldWithMode
            label="Valore destro"
            fieldKey="right_value"
            value={(config.right_value as string | number) || ''}
            onChange={(v) => onChange('right_value', v)}
            availableVariables={availableVariables}
            placeholder="es: 50"
            tooltip="Il valore con cui confrontare"
            allowAll={false}
          />
        </>
      )}

      {(config.operator === 'is_empty' || config.operator === 'is_not_empty') && (
        <ConfigFieldWithMode
          label="Valore da verificare"
          fieldKey="left_value"
          value={(config.left_value as string | number) || ''}
          onChange={(v) => onChange('left_value', v)}
          availableVariables={availableVariables}
          placeholder="es: lead.email"
          tooltip="Il campo da verificare se è vuoto o meno"
          allowAll={false}
        />
      )}

      {config.operator === 'expression' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Espressione</Label>
          <Input
            value={(config.condition as string | number) || ''}
            onChange={(e) => onChange('condition', e.target.value)}
            placeholder="{{lead.score}} > 50 && {{lead.priority}} == 'high'"
            className="font-mono bg-gray-50 dark:bg-gray-900"
          />
          <p className="text-xs text-gray-500">Usa operatori: &amp;&amp; (e), || (o), == (uguale), != (diverso), &gt;, &lt;</p>
        </div>
      )}
    </div>
  );
}

export function SwitchCaseConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  const cases = (config.cases as string[] | undefined) || ['caso1', 'caso2'];

  const addCase = () => {
    onChange('cases', [...cases, `caso${cases.length + 1}`]);
  };

  const removeCase = (index: number) => {
    onChange('cases', cases.filter((_: string, i: number) => i !== index));
  };

  const updateCase = (index: number, value: string) => {
    const newCases = [...cases];
    newCases[index] = value;
    onChange('cases', newCases);
  };

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Valore da valutare"
        fieldKey="value"
        value={(config.value as string | number) || ''}
        onChange={(v) => onChange('value', v)}
        availableVariables={availableVariables}
        placeholder="{{status}}"
        tooltip="Il valore che verrà confrontato con i casi"
        allowAll={false}
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Casi</Label>
        {cases.map((c: string, index: number) => (
          <div key={index} className="flex gap-2">
            <Input
              value={c}
              onChange={(e) => updateCase(index, e.target.value)}
              placeholder={`Caso ${index + 1}`}
              className="bg-gray-50 dark:bg-gray-900"
            />
            {cases.length > 2 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeCase(index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCase}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi caso
        </Button>
        <p className="text-xs text-gray-500">Il valore verrà confrontato con ogni caso + un branch "Default" automatico</p>
      </div>
    </div>
  );
}

export function LoopForeachConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Array da iterare"
        fieldKey="array"
        value={(config.array as string | number) || ''}
        onChange={(v) => onChange('array', v)}
        availableVariables={availableVariables.filter(v => v.type === 'array' || v.type === 'object')}
        placeholder="{{contacts}}"
        tooltip="L'array su cui eseguire il loop"
        allowAll={false}
        compatibleTypes={['array', 'object']}
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome variabile elemento</Label>
        <Input
          value={(config.item_variable as string | number) || 'item'}
          onChange={(e) => onChange('item_variable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="item"
          className="font-mono bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">
          Ogni elemento sarà accessibile come {`{{${config.item_variable || 'item'}}}`}
        </p>
      </div>

      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Variabili disponibili nel loop:</p>
        <ul className="text-xs text-blue-600 dark:text-blue-400 mt-1 space-y-0.5">
          <li><code>{`{{${config.item_variable || 'item'}}}`}</code> - Elemento corrente</li>
          <li><code>{`{{index}}`}</code> - Indice (da 0)</li>
          <li><code>{`{{is_first}}`}</code> - È il primo?</li>
          <li><code>{`{{is_last}}`}</code> - È l'ultimo?</li>
        </ul>
      </div>
    </div>
  );
}

export function LoopWhileConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Condizione</Label>
        <Input
          value={(config.condition as string | number) || ''}
          onChange={(e) => onChange('condition', e.target.value)}
          placeholder="{{retry_count}} < 3"
          className="font-mono bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Il loop continua finché questa condizione è vera</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Massimo iterazioni</Label>
        <Input
          type="number"
          min={1}
          max={1000}
          value={(config.max_iterations as string | number) || 100}
          onChange={(e) => onChange('max_iterations', parseInt(e.target.value) || 100)}
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Limite di sicurezza per evitare loop infiniti</p>
      </div>
    </div>
  );
}

export function DelayWaitConfig({ config, onChange }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Durata</Label>
          <Input
            type="number"
            min={1}
            value={(config.duration as string | number) || 5}
            onChange={(e) => onChange('duration', parseInt(e.target.value) || 5)}
            className="bg-gray-50 dark:bg-gray-900"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Unità</Label>
          <Select
            value={(config.unit as string) || 'seconds'}
            onValueChange={(v) => onChange('unit', v)}
          >
            <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="seconds">Secondi</SelectItem>
              <SelectItem value="minutes">Minuti</SelectItem>
              <SelectItem value="hours">Ore</SelectItem>
              <SelectItem value="days">Giorni</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Il workflow attenderà {(config.duration as number | string | undefined) || 5} {config.unit === 'seconds' ? 'secondi' : config.unit === 'minutes' ? 'minuti' : config.unit === 'hours' ? 'ore' : 'giorni'} prima di continuare
      </p>
    </div>
  );
}

export function DelayUntilConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Data e ora"
        fieldKey="datetime"
        value={(config.datetime as string | number) || ''}
        onChange={(v) => onChange('datetime', v)}
        availableVariables={availableVariables.filter(v => v.type === 'date' || v.type === 'string')}
        placeholder="2025-01-22T09:00:00"
        tooltip="Formato ISO 8601 o variabile con data"
        allowAll={false}
        compatibleTypes={['date', 'string']}
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fuso orario</Label>
        <Select
          value={(config.timezone as string) || 'Europe/Rome'}
          onValueChange={(v) => onChange('timezone', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Europe/Rome">Roma (Italia)</SelectItem>
            <SelectItem value="Europe/London">Londra (UK)</SelectItem>
            <SelectItem value="America/New_York">New York (USA)</SelectItem>
            <SelectItem value="UTC">UTC</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function ParallelSplitConfig({ config, onChange }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Numero di branch paralleli</Label>
        <Select
          value={String(config.branches || 2)}
          onValueChange={(v) => onChange('branches', parseInt(v))}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="2">2 branch</SelectItem>
            <SelectItem value="3">3 branch</SelectItem>
            <SelectItem value="4">4 branch</SelectItem>
            <SelectItem value="5">5 branch</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Tutti i branch verranno eseguiti contemporaneamente. 
          Usa "Parallel Join" per attendere il completamento di tutti.
        </p>
      </div>
    </div>
  );
}

export function ParallelJoinConfig({ config, onChange }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Timeout (secondi)</Label>
        <Input
          type="number"
          min={10}
          max={3600}
          value={(config.timeout as string | number) || 300}
          onChange={(e) => onChange('timeout', parseInt(e.target.value) || 300)}
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Tempo massimo di attesa per tutti i branch</p>
      </div>

      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Questo nodo attende che tutti i branch paralleli siano completati prima di continuare.
        </p>
      </div>
    </div>
  );
}

export function GotoNodeConfig({ config, onChange, allNodes = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nodo destinazione</Label>
        <Select
          value={(config.target_node_id as string) || ''}
          onValueChange={(v) => onChange('target_node_id', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue placeholder="Seleziona nodo..." />
          </SelectTrigger>
          <SelectContent>
            {allNodes.map(node => (
              <SelectItem key={node.id} value={node.id}>
                {node.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Massimo salti</Label>
        <Input
          type="number"
          min={1}
          max={100}
          value={(config.max_jumps as string | number) || 10}
          onChange={(e) => onChange('max_jumps', parseInt(e.target.value) || 10)}
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Limite per evitare loop infiniti</p>
      </div>
    </div>
  );
}

export function EndWorkflowConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Stato finale</Label>
        <Select
          value={(config.status as string) || 'completed'}
          onValueChange={(v) => onChange('status', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="completed">Completato con successo</SelectItem>
            <SelectItem value="cancelled">Annullato</SelectItem>
            <SelectItem value="failed">Fallito</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ConfigFieldWithMode
        label="Messaggio finale"
        fieldKey="message"
        value={(config.message as string | number) || ''}
        onChange={(v) => onChange('message', v)}
        availableVariables={availableVariables}
        placeholder="Workflow completato per {{lead.first_name}}"
        tooltip="Messaggio opzionale di riepilogo"
        allowAll={false}
        inputType="textarea"
      />
    </div>
  );
}

export function ErrorHandlerConfig({ config, onChange }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tentativi di retry</Label>
        <Select
          value={String(config.retry_count || 0)}
          onValueChange={(v) => onChange('retry_count', parseInt(v))}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Nessun retry</SelectItem>
            <SelectItem value="1">1 tentativo</SelectItem>
            <SelectItem value="2">2 tentativi</SelectItem>
            <SelectItem value="3">3 tentativi</SelectItem>
            <SelectItem value="5">5 tentativi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {((config.retry_count as number | undefined) || 0) > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Attesa tra retry (secondi)</Label>
          <Input
            type="number"
            min={1}
            max={300}
            value={(config.retry_delay as string | number) || 5}
            onChange={(e) => onChange('retry_delay', parseInt(e.target.value) || 5)}
            className="bg-gray-50 dark:bg-gray-900"
          />
        </div>
      )}

      <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
        <p className="text-sm text-orange-700 dark:text-orange-300">
          Se si verifica un errore, questo nodo lo cattura e può ritentare. 
          Ha due uscite: "Successo" e "Errore".
        </p>
      </div>
    </div>
  );
}

export function SetVariableConfig({ config, onChange, availableVariables = [] }: FlowControlConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome variabile</Label>
        <Input
          value={(config.variable_name as string | number) || ''}
          onChange={(e) => onChange('variable_name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="my_variable"
          className="font-mono bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Solo lettere, numeri e underscore</p>
      </div>

      <ConfigFieldWithMode
        label="Valore"
        fieldKey="value"
        value={(config.value as string | number) || ''}
        onChange={(v) => onChange('value', v)}
        availableVariables={availableVariables}
        placeholder="{{lead.score}} + 10"
        tooltip="Il valore da assegnare alla variabile"
        allowAll={false}
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</Label>
        <Select
          value={(config.type as string) || 'expression'}
          onValueChange={(v) => onChange('type', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">Stringa</SelectItem>
            <SelectItem value="number">Numero</SelectItem>
            <SelectItem value="boolean">Vero/Falso</SelectItem>
            <SelectItem value="json">JSON</SelectItem>
            <SelectItem value="expression">Espressione</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(config.variable_name as string | undefined) && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm text-green-700 dark:text-green-300">
            La variabile sarà accessibile come: <code className="font-mono">{`{{${config.variable_name}}}`}</code>
          </p>
        </div>
      )}
    </div>
  );
}

export const FLOW_CONTROL_CONFIG_COMPONENTS: Record<string, React.FC<FlowControlConfigProps>> = {
  if_condition: IfConditionConfig,
  switch_case: SwitchCaseConfig,
  loop_foreach: LoopForeachConfig,
  loop_while: LoopWhileConfig,
  delay_wait: DelayWaitConfig,
  delay_until: DelayUntilConfig,
  parallel_split: ParallelSplitConfig,
  parallel_join: ParallelJoinConfig,
  goto_node: GotoNodeConfig,
  end_workflow: EndWorkflowConfig,
  error_handler: ErrorHandlerConfig,
  set_variable: SetVariableConfig,
};
